import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  History,
  ArrowDownCircle,
  ArrowUpCircle,
  RotateCcw,
  FileMinus,
  Wrench,
  Lock,
  RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const EVENT_META = {
  payment: {
    label: 'Payment',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Icon: ArrowDownCircle,
  },
  reversal: {
    label: 'Reversal',
    color: 'bg-rose-50 text-rose-700 border-rose-200',
    Icon: RotateCcw,
  },
  adjustment: {
    label: 'Adjustment',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    Icon: ArrowUpCircle,
  },
  manual_correction: {
    label: 'Manual Correction',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    Icon: Wrench,
  },
  writeoff: {
    label: 'Write-off',
    color: 'bg-slate-50 text-slate-700 border-slate-200',
    Icon: FileMinus,
  },
  legacy_snapshot: {
    label: 'Legacy Snapshot',
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    Icon: Lock,
  },
};

/**
 * PaymentTimelineCard — append-only ledger event history for a claim.
 *
 * Renders in chronological order (event_seq ascending):
 *  • Signed paid amount + source (era_auto / manual_insurance / self_pay / writeoff / legacy_snapshot)
 *  • Event type chip (payment / reversal / adjustment / manual_correction / writeoff / legacy_snapshot)
 *  • Payer + ICN + reason_category
 *  • Linkage: reversal rows show which prior event they reverse
 *
 * Also surfaces the claim's `financials_cache` rollup (derived from ledger) at the top.
 */
export const PaymentTimelineCard = ({ claimId, claimNumber }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!claimId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/claims/${claimId}/payment-events`);
      setData(resp.data);
    } catch (e) {
      // Non-billing users get 403 — hide the card silently by setting an empty state
      if (e?.response?.status === 403) {
        setData(null);
        setError('forbidden');
      } else {
        setError(e?.response?.data?.detail || 'Failed to load payment timeline');
      }
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    load();
  }, [load]);

  const eventsById = useMemo(() => {
    const map = {};
    (data?.events || []).forEach((ev) => { map[ev.id] = ev; });
    return map;
  }, [data]);

  if (error === 'forbidden') return null;

  const events = data?.events || [];
  const cache = data?.financials_cache || {};

  return (
    <Card className="border-l-4 border-l-teal-500" data-testid="payment-timeline-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <History className="h-4 w-4 text-teal-600" />
            Payment Timeline
            {claimNumber ? <span className="text-xs text-gray-500 font-normal">· Claim {claimNumber}</span> : null}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="payment-timeline-refresh-btn"
            className="h-8"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Rollup strip (derived from ledger) */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-gray-50 rounded-md text-sm"
          data-testid="payment-timeline-rollup"
        >
          <div>
            <div className="text-xs text-gray-500">Insurance Paid</div>
            <div className="font-semibold text-emerald-700">{fmtMoney(cache.insurance_paid)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Self Pay Received</div>
            <div className="font-semibold text-emerald-700">{fmtMoney(cache.self_pay_received)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Client Balance</div>
            <div className="font-semibold text-amber-700">{fmtMoney(cache.patient_balance)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Insurance Balance</div>
            <div className="font-semibold text-amber-700">{fmtMoney(cache.insurance_balance)}</div>
          </div>
        </div>

        {loading && events.length === 0 && (
          <div className="text-center py-6 text-sm text-gray-500">Loading timeline…</div>
        )}
        {!loading && events.length === 0 && (
          <div
            className="text-center py-6 text-sm text-gray-500"
            data-testid="payment-timeline-empty"
          >
            No payment events yet. Events are created automatically when an ERA posts, a payment is recorded, or a write-off is applied.
          </div>
        )}
        {error && error !== 'forbidden' && (
          <div className="text-sm text-rose-600" data-testid="payment-timeline-error">{error}</div>
        )}

        {events.length > 0 && (
          <ol className="relative border-l-2 border-gray-200 ml-3 space-y-4" data-testid="payment-timeline-list">
            {events.map((ev) => {
              const meta = EVENT_META[ev.event_type] || {
                label: ev.event_type,
                color: 'bg-gray-100 text-gray-700 border-gray-200',
                Icon: History,
              };
              const Icon = meta.Icon;
              const isNegative = Number(ev.paid_amount || 0) < 0;
              const linkedSource = ev.reverses_event_id ? eventsById[ev.reverses_event_id] : null;
              return (
                <li
                  key={ev.id}
                  className="ml-4 pl-4"
                  data-testid={`payment-timeline-event-${ev.event_seq}`}
                >
                  <span className="absolute -left-2 flex items-center justify-center w-4 h-4 bg-white border-2 border-gray-300 rounded-full">
                    <Icon className={`h-3 w-3 ${isNegative ? 'text-rose-600' : 'text-emerald-600'}`} />
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`${meta.color} text-xs`}>
                      #{ev.event_seq} · {meta.label}
                    </Badge>
                    <span className={`font-mono font-semibold ${isNegative ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {fmtMoney(ev.paid_amount)}
                    </span>
                    {ev.source && (
                      <span className="text-xs text-gray-500">via {ev.source.replace(/_/g, ' ')}</span>
                    )}
                    {ev.is_voided && (
                      <Badge className="bg-gray-600 text-white text-xs">VOIDED</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                    <div>
                      <span className="text-gray-400">Posted:</span> {fmtDate(ev.posted_at)}
                      {ev.posted_by_name && <span> · {ev.posted_by_name}</span>}
                    </div>
                    {ev.payer_name && (
                      <div>
                        <span className="text-gray-400">Payer:</span> {ev.payer_name}
                        {ev.payer_claim_number && (
                          <span className="font-mono"> · ICN {ev.payer_claim_number}</span>
                        )}
                        {ev.era_check_number && (
                          <span> · Check #{ev.era_check_number}</span>
                        )}
                      </div>
                    )}
                    {ev.reason_category && ev.reason_category !== 'other' && (
                      <div>
                        <span className="text-gray-400">Reason:</span>{' '}
                        <span className="capitalize">{ev.reason_category.replace(/_/g, ' ')}</span>
                        {ev.clp_status_code && <span className="font-mono"> · CLP {ev.clp_status_code}</span>}
                      </div>
                    )}
                    {linkedSource && (
                      <div className="text-rose-600">
                        ↳ Reverses event #{linkedSource.event_seq} ({fmtMoney(linkedSource.paid_amount)})
                      </div>
                    )}
                    {ev.notes && (
                      <div className="italic text-gray-500">"{ev.notes}"</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentTimelineCard;
